"use client";

import { ComponentWithAnalyticsGlobalField } from "@solarwinds-webbiz/types";
import { FC, useMemo } from "react";
import InteractiveCard from "./InteractiveCard";
import { Select } from "@/components/Select";
import { Dropdown as DropdownType } from "@/types/General/Dropdown";

export type Challenge = {
  key: string;
  value: string;
  analytics?: ComponentWithAnalyticsGlobalField;
};

type ChallengeDropdownSectionProps = {
  header?: string;
  description?: string;
  optionalText?: string;
  dropdown?: {
    name?: string;
    removeChallengeCta?: string;
    challenges?: Challenge[];
    placeholder?: string;
  };
  selectedChallenge?: Challenge | null;
  setSelectedChallenge?: (arg: Challenge | null) => void;
};

const ChallengeDropdownSection: FC<ChallengeDropdownSectionProps> = ({
  optionalText,
  header,
  description,
  dropdown,
  selectedChallenge,
  setSelectedChallenge,
}) => {
  const dropdownOptions = useMemo<DropdownType[]>(() => {
    const options: DropdownType[] = [];

    if (selectedChallenge?.key && dropdown?.removeChallengeCta) {
      options.push({
        label: dropdown.removeChallengeCta,
        value: "__remove_challenges__",
        className:
          "text-swdc-blue-500 outline-swdc-blue-400 visited:text-swdc-blue-500 hover:bg-swdc-gray-50 flex w-full cursor-pointer rounded-t-lg bg-[#fbfbfb] px-4 py-2 text-left font-medium underline outline outline-0 outline-offset-[-2px]",
      });
    }
    if (dropdown?.challenges) {
      options.push(
        ...dropdown.challenges.map((challenge) => ({
          label: challenge.value,
          value: challenge.key,
        })),
      );
    }

    return options;
  }, [dropdown, selectedChallenge]);

  const selectedDropdownValue = useMemo<DropdownType | null>(() => {
    if (selectedChallenge?.key) {
      return {
        label: selectedChallenge.value,
        value: selectedChallenge.key,
      };
    }
    return {
      label: dropdown?.placeholder || "Select a challenge",
      value: "__placeholder__",
    };
  }, [selectedChallenge, dropdown?.placeholder]);

  const handleChange = (option: DropdownType | null) => {
    if (!setSelectedChallenge) return;

    if (option?.value === "__remove_challenges__") {
      setSelectedChallenge(null);
      return;
    }

    const challenge = dropdown?.challenges?.find(
      (c) => c.key === option?.value,
    );
    if (challenge) {
      setSelectedChallenge(challenge);
    }
  };
  return (
    <InteractiveCard header={header} optionalText={optionalText}>
      <div className="flex basis-full flex-col items-center justify-between">
        {description && (
          <div className="text-body-m text-swdc-gray-400 my-8 w-full font-normal">
            {description}
          </div>
        )}
        <div className="max-w-95 self-stretch">
          <Select
            variant="large rectangular"
            options={dropdownOptions}
            value={selectedDropdownValue}
            onChange={handleChange}
          />
        </div>
      </div>
    </InteractiveCard>
  );
};

export default ChallengeDropdownSection;
